import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Megaphone, Radio, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { formatTimestamp } from '../../lib/format';
import { Button, Card, ErrorNotice, Field, Input, Pill, Spinner } from '../../components/ui';

interface StatusUpdate {
  id: string;
  severity: 'info' | 'warn' | 'outage';
  title: string;
  body: string | null;
  by: string | null;
  at: number;
}

const SEVERITY_TONE = { info: 'info', warn: 'warn', outage: 'danger' } as const;

/**
 * The status page's home in Settings.
 *
 * The page itself is deliberately NOT this app: nginx serves it from static
 * files a systemd timer keeps fresh, so it keeps answering when SimpleACS is
 * the thing that died. What lives here is everything around it — where it is,
 * what it covers, and the operator's voice on it: posting the status updates
 * subscribers read during an incident.
 */
export function StatusPageTab() {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);
  const [severity, setSeverity] = useState<StatusUpdate['severity']>('info');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pageUrl = `${window.location.origin}/status/`;
  const jsonUrl = `${window.location.origin}/status/data/status.json`;

  const updates = useQuery({
    queryKey: ['status-updates'],
    queryFn: () => api.get<{ items: StatusUpdate[] }>('/status-updates'),
  });

  const post = useMutation({
    mutationFn: () =>
      api.post<StatusUpdate>('/status-updates', { severity, title: title.trim(), body: body.trim() || null }),
    onSuccess: async () => {
      setTitle('');
      setBody('');
      setSeverity('info');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['status-updates'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not post the update'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/status-updates/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['status-updates'] }),
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not take it down'),
  });

  const copy = async (what: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 2500);
  };

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <Radio className="size-4 text-muted" />
            The status page
          </span>
        }
        action={
          <a
            href={pageUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-link hover:underline"
          >
            Open it <ExternalLink className="size-3.5" />
          </a>
        }
      >
        <div className="space-y-3 p-5">
          <p className="text-sm text-muted">
            A public page for the services SimpleACS runs on — the API, the ACS, its database,
            the web server and the tunnels. Not the managed network: OLTs and ONUs are reported
            inside the platform, not here. It is served by nginx from static files that a
            separate checker refreshes every minute, so it keeps answering even when SimpleACS
            itself is down — which is exactly when a status page earns its keep.
          </p>
          <pre className="overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-ink">{pageUrl}</pre>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => copy('page', pageUrl)}>
              {copied === 'page' ? 'Copied' : 'Copy the link'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => copy('json', jsonUrl)}>
              {copied === 'json' ? 'Copied' : 'Copy the JSON URL'}
            </Button>
          </div>
        </div>
      </Card>

      <Card
        title={
          <span className="flex items-center gap-2">
            <Megaphone className="size-4 text-muted" />
            Post a status update
          </span>
        }
      >
        <div className="space-y-4 p-5">
          {error && <ErrorNotice error={error} />}
          <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
            <Field label="Severity">
              <select
                className="input"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as StatusUpdate['severity'])}
              >
                <option value="info">Info</option>
                <option value="warn">Warning</option>
                <option value="outage">Outage</option>
              </select>
            </Field>
            <Field label="Title" hint="The one line a subscriber reads first">
              <Input
                value={title}
                maxLength={120}
                placeholder="Planned maintenance tonight, 22:00–23:00"
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Details" hint="Optional. Plain text; line breaks survive.">
            <textarea
              className="input min-h-20 py-2"
              value={body}
              maxLength={2000}
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>
          <div className="flex justify-end">
            <Button
              variant="primary"
              disabled={!title.trim()}
              loading={post.isPending}
              onClick={() => post.mutate()}
            >
              Post to the status page
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Posted updates">
        {updates.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : (updates.data?.items.length ?? 0) === 0 ? (
          <p className="p-5 text-sm text-muted">
            Nothing posted. The page shows the last 30 days of updates; older ones stay in the
            archive here.
          </p>
        ) : (
          <div className="divide-y divide-line">
            {updates.data!.items.map((u) => (
              <div key={u.id} className="flex items-start justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill state tone={SEVERITY_TONE[u.severity]}>{u.severity}</Pill>
                    <span className="font-medium text-ink">{u.title}</span>
                  </div>
                  {u.body && <p className="mt-1 whitespace-pre-wrap text-sm text-muted">{u.body}</p>}
                  <p className="mt-1 text-[11px] text-faint">
                    {formatTimestamp(u.at)}
                    {u.by ? ` · ${u.by}` : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="size-3.5" />}
                  loading={remove.isPending && remove.variables === u.id}
                  title="Take it off the page"
                  onClick={() => remove.mutate(u.id)}
                >
                  Take down
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
